import { max } from 'lodash';
import { indexOf, min, set } from 'lodash/fp';
import Slider from 'rc-slider';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch } from '../../../../hooks/react-redux';
import { DATE_FORMAT, formatDate } from '../../../../utils/luxon';
import { CustomTooltip } from '../../../shared/tooltip';
import styles from './AIManager.module.scss';
import { DocumentQueryDB, setSelectedHistoricalDocumentId } from './store';

const { french, fixedWhite, grey } = styles;

interface MarkerProps {
    configTimeline: DocumentQueryDB;
    timelineLength: number;
    isCurrent: boolean;
}

const Marker: React.FC<MarkerProps> = ({ configTimeline, timelineLength, isCurrent }) => {
    const overlay = isCurrent ? 'Current' : `${formatDate(configTimeline.updatedDate!, DATE_FORMAT)}`;

    if (timelineLength < 13) {
        return <div className={styles.sliderDateMarker}>{overlay}</div>;
    }

    return (
        <CustomTooltip overlayText={overlay} placement='top'>
            <div className={styles.sliderMarker} />
        </CustomTooltip>
    );
};

interface HistoricalAIQueriesTimelineProps {
    timeline: DocumentQueryDB[];
    selectedId: number | null;
    timelineDisabled: boolean;
}

export const HistoricalAIQueriesTimeline: React.FC<HistoricalAIQueriesTimelineProps> = ({ timeline, selectedId, timelineDisabled = false }) => {

    const mapAllInstances = useCallback((timeline: DocumentQueryDB[]) => timeline.reduce((acc, history, index) => {
        const key = index + 1;
        return set(key, <Marker configTimeline={history} timelineLength={timeline.length} isCurrent={key === timeline.length} />, acc);
    }, {}), []);

    const historicalInstances = useMemo(() => mapAllInstances(timeline), [timeline, mapAllInstances]);

    const dispatch = useAppDispatch();

    const minValue = min((Object.keys(historicalInstances).map(val => parseInt(val))));
    const maxValue = max((Object.keys(historicalInstances).map(val => parseInt(val))));
    const minimum = minValue && maxValue !== minValue ? minValue : 0;
    const selected = useMemo(() => timeline.find(({ documentQueryId }) => documentQueryId === selectedId), [timeline, selectedId]);

    const value = useMemo(() => indexOf(selected, timeline) + 1, [timeline, selected]);

    const onChange = (value: number | number[]) => {
        const index = (value as number) - 1;
        const clickedInstance = timeline[index];
        if (!timelineDisabled && clickedInstance && clickedInstance.documentQueryId !== selectedId) {
            dispatch(setSelectedHistoricalDocumentId(clickedInstance.documentQueryId));
        }
    };

    return (
        <div className={styles.timelineWrapper}>
            {timeline.length ?
                <Slider
                    min={minimum}
                    marks={historicalInstances}
                    onChange={onChange}
                    defaultValue={maxValue}
                    value={value}
                    max={maxValue}
                    step={1}
                    trackStyle={{ backgroundColor: french }}
                    railStyle={{ backgroundColor: grey }}
                    handleStyle={{ backgroundColor: fixedWhite, border: `1px solid ${french}`, height: '16px', width: '16px', marginTop: '-6px', opacity: 1 }}
                    dotStyle={{ backgroundColor: fixedWhite, border: `1px solid ${french}`, cursor: timelineDisabled ? 'not-allowed' : 'pointer' }}
                /> : <div style={{ height: '14px' }} />}
        </div>
    );
};
